import CryptoJS from 'crypto-js';

class DigestRealm {
    scheme = null; // we just echo the scheme, to allow for 'Digest', 'X-Digest', 'JDigest' etc
    nonce = null; // server issued nonce
    realm = null; // server issued realm
    qop = null; // "quality of protection" - '' or 'auth' or 'auth-int'
    response = null; // hashed response to server challenge
    opaque = null; // hashed response to server challenge
    nc = 1; // nonce count - increments with each request used with the same nonce
    cnonce = null; // client nonce
    timeout = 20000; // timeout

    debug = false;

    constructor({ path, username, password }) {
        this.path = path;
        this.username = username;
        this.password = password;

        this.fetch = this.fetch.bind(this);

        this._throttle = 0;
    }

    log = (...args) => this.debug && console.log(...args);

    generateCnonce = () => {
        var characters = 'abcdef0123456789';
        var token = '';
        for (var i = 0; i < 16; i++) {
            var randNum = Math.round(Math.random() * characters.length);
            token += characters.substr(randNum, 1);
        }
        return token;
    };

    firstRequest = (path, { method, headers, body, timeout }) => {
        return new Promise((resolve, reject) => {
            const request = new XMLHttpRequest();
            request.open(method, path, true);

            request.timeout = timeout || this.timeout;

            Object.keys(headers).forEach(header =>
                request.setRequestHeader(header, headers[header])
            );

            request.onreadystatechange = () => {
                // 2: received headers,  3: loading, 4: done
                if (request.readyState === 2) {
                    let responseHeaders = request.getAllResponseHeaders();
                    responseHeaders = responseHeaders.split('\n');
                    // get authenticate header
                    var digestHeaders;
                    for (let i = 0; i < responseHeaders.length; i++) {
                        if (
                            responseHeaders[i].match(/barix-authenticate/i) !=
                            null
                        ) {
                            digestHeaders = responseHeaders[i];
                        }
                    }

                    if (digestHeaders != null) {
                        // parse auth header and get digest auth keys
                        digestHeaders = digestHeaders.slice(
                            digestHeaders.indexOf(':') + 1,
                            -1
                        );
                        digestHeaders = digestHeaders.split(',');
                        this.scheme = digestHeaders[0].split(/\s/)[1];
                        for (let i = 0; i < digestHeaders.length; i++) {
                            var equalIndex = digestHeaders[i].indexOf('='),
                                key = digestHeaders[i].substring(0, equalIndex),
                                val = digestHeaders[i].substring(
                                    equalIndex + 1
                                );
                            val = val.replace(/['"]+/g, '');
                            // find realm
                            if (key.match(/realm/i) != null) {
                                this.realm = val;
                            }
                            // find nonce
                            if (key.match(/nonce/i) != null) {
                                this.nonce = val;
                            }
                            // find opaque
                            if (key.match(/opaque/i) != null) {
                                this.opaque = val;
                            }
                            // find QOP
                            if (key.match(/qop/i) != null) {
                                this.qop = val;
                            }
                        }
                        // client generated keys
                        this.cnonce = this.generateCnonce();
                        this.nc++;
                        // if logging, show headers received:
                        this.log('received headers:');
                        this.log('	realm: ' + this.realm);
                        this.log('	nonce: ' + this.nonce);
                        this.log('	opaque: ' + this.opaque);
                        this.log('	qop: ' + this.qop);

                        // now we can make an authenticated request
                        // this.makeAuthenticatedRequest();
                        resolve(
                            this.request(path, {
                                method,
                                body,
                                headers,
                                timeout
                            })
                        );
                    }
                }
                if (request.readyState === 4) {
                    if (request.status === 200) {
                        this.log('Authentication not required for ' + path);
                        resolve(request.responseText);
                    }
                }
            };

            request.onerror = () => {
                reject('Request failed');
            };

            request.send(body);
        });
    };

    formulateResponse = ({ path, method }) => {
        var HA1 = CryptoJS.MD5(
            this.username + ':' + this.realm + ':' + this.password
        ).toString();

        var HA2 = CryptoJS.MD5(method + ':' + path).toString();
        var response = CryptoJS.MD5(
            HA1 +
                ':' +
                this.nonce +
                ':' +
                ('00000000' + this.nc).slice(-8) +
                ':' +
                this.cnonce +
                ':' +
                this.qop +
                ':' +
                HA2
        ).toString();
        return response;
    };

    request = async (path, { method, headers, body, request, timeout }) => {
        return new Promise((resolve, reject) => {
            this.response = this.formulateResponse({ path, method });

            if (!request) {
                request = new XMLHttpRequest();
            }
            request.open(method, path, true);

            request.timeout = timeout || this.timeout;
            var digestAuthHeader =
                this.scheme +
                ' ' +
                'username="' +
                this.username +
                '", ' +
                'realm="' +
                this.realm +
                '", ' +
                'nonce="' +
                this.nonce +
                '", ' +
                'uri="' +
                path +
                '", ' +
                'response="' +
                this.response +
                '", ' +
                'opaque="' +
                this.opaque +
                '", ' +
                'qop=' +
                this.qop +
                ', ' +
                'nc=' +
                ('00000000' + this.nc).slice(-8) +
                ', ' +
                'cnonce="' +
                this.cnonce +
                '"';
            request.setRequestHeader('Authorization', digestAuthHeader);

            Object.keys(headers).forEach(header =>
                request.setRequestHeader(header, headers[header])
            );

            this.log('digest auth header response to be sent:');
            this.log(digestAuthHeader);

            // increment nonce count
            this.nc++;

            request.onload = () => {
                if (request.status >= 200 && request.status < 400) {
                    // success
                    resolve(request.responseText);
                } else {
                    // failure
                    if (request.status === 401) {
                        this.nonce = null;
                    }
                    reject('Request failed');
                }
            };

            // handle errors
            request.onerror = () => {
                this.log(
                    `Error (${
                        request.status
                    }) on authenticated request to ${path}`
                );
                if (request.status === 401) {
                    this.nonce = null;
                }
                reject('Request failed');
            };

            request.send(body);
            this.log('Authenticated request to ', path);
        });
    };

    async fetch(path, args) {
        args = {
            method: 'GET',
            headers: [],
            ...args
        };
        if (this._firstRequest) {
            await this._firstRequest;
        }

        if (!this.nonce) {
            this._firstRequest = this.firstRequest(path, args);
            const res = await this._firstRequest;
            this._firstRequest = null;
            return res;
        } else {
            return this.request(path, args);
        }
    }
}

export default DigestRealm;



// WEBPACK FOOTER //
// ./src/DigestRealm.js